import { useTranslation } from 'react-i18next';
import { Monitor, Moon, Palette, Sun } from 'lucide-react';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useTheme, type ThemeChoice } from '@/lib/theme';

/**
 * Appearance = the two device-level preferences Claude exposes: language and
 * theme.
 *
 * Le thème était ici en LECTURE SEULE, un badge « Clair », avec un commentaire
 * qui disait que « la DA aikka ne livre qu'un thème clair et n'a pas de tokens
 * sombres ». C'était honnête au moment où c'était écrit ; ça ne l'est plus :
 * `os.css` porte un thème sombre complet, la topbar de l'OS le bascule déjà, et
 * ce réglage disait au client le contraire de ce que le produit sait faire.
 *
 * Trois choix, deux peaux (DESIGN.md §7 : « deux thèmes, pas trois »).
 * « Système » n'est pas un bonus : c'est le DÉFAUT correct, parce que
 * `prefers-color-scheme` est ce que l'opérateur a déjà dit à son OS — et parce
 * que sans lui, une fois un choix posé, on ne peut plus revenir au suivi de
 * l'OS. C'est le retour en arrière qui manquait
 * (docs/dark-theme-scope.md §5).
 *
 * Langue et thème ne se rangent pas au même endroit, et le texte le dit :
 * la langue est enregistrée sur l'APPAREIL (`aikka-lang`), le thème sur le
 * COMPTE (§7), avec `localStorage` comme simple canal d'avant-peinture.
 */

const THEME_OPTIONS: ReadonlyArray<{
  value: ThemeChoice;
  labelKey: string;
  Icon: typeof Sun;
}> = [
  { value: 'light', labelKey: 'settings.appearance.themeLight', Icon: Sun },
  { value: 'dark', labelKey: 'settings.appearance.themeDark', Icon: Moon },
  { value: 'system', labelKey: 'settings.appearance.themeSystem', Icon: Monitor },
];

export function SettingsAppearanceSection() {
  const { t } = useTranslation('dashboard');
  const { choice, setChoice } = useTheme();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">{t('settings.appearance.title')}</CardTitle>
        </div>
        <CardDescription>{t('settings.appearance.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('settings.appearance.language')}</p>
            <p className="text-xs text-muted-foreground">{t('settings.appearance.languageHint')}</p>
          </div>
          <LanguageSwitcher />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('settings.appearance.theme')}</p>
            <p className="text-xs text-muted-foreground">{t('settings.appearance.themeHint')}</p>
          </div>
          <div
            role="radiogroup"
            aria-label={t('settings.appearance.theme')}
            data-testid="group-theme"
            className="shrink-0 inline-flex items-center gap-1 rounded-control border border-border bg-card p-1"
          >
            {THEME_OPTIONS.map(({ value, labelKey, Icon }) => {
              const active = choice === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-testid={`button-theme-${value}`}
                  onClick={() => setChoice(value)}
                  className={`inline-flex items-center gap-1.5 rounded-control px-2.5 py-1 text-xs transition-colors ${
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
